import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./ExamplesPage.css";
import "./landing/landing.css";
import { useAuthContext } from "./components/AuthProvider";
import UniversalLayout from "./components/UniversalLayout";
import PaywallModal from "./components/PaywallModal";
import AgentAutoReloadModal from "./components/AgentAutoReloadModal";
import {
  initializeProject,
  createBlankProject,
  startAgentCompose,
  startMultiAgentCompose,
  setupBilling,
  getBillingUsage,
  confirmCreditPurchase,
  confirmSubscription,
  getAgentEligibility,
  preflightAttachmentReferences,
  updateCreditSettings,
  sendCAPIEvent,
} from "./lib/api";
import { trackMetaEvent } from "./lib/meta";
import { getAdAttribution } from "./lib/adAttribution";
import { useTheme } from "./hooks/useTheme";
import { DEFAULT_COMPOSER_LLM_MODEL } from "./composerModels";
import { appendComposerAspectRatioToPrompt } from "./lib/aspectRatioOptions";
import { mediaModelRequestOptions } from "./components/composer/MediaModelPicker";
import {
  toAttachmentReferences,
  useComposerReferencePicker,
} from "./components/ComposerReferencePicker";
import { useAttachmentPreflightConfirmation } from "./components/AttachmentPreflightDialog";
import { composerAttachmentChip, validateComposerFiles } from "./components/composer/attachmentValidation";
import LandingNavigation from "./landing/LandingNavigation";
import LandingHero from "./landing/LandingHero";
import HeroFloatingIcons from "./landing/HeroFloatingIcons";
import LandingComposer from "./landing/LandingComposer";
import LandingProjectRuns from "./landing/LandingProjectRuns";
import OutputShowcase from "./landing/OutputShowcase";
import ComposerAgentShowcase from "./landing/ComposerAgentShowcase";
import TaglineReveal from "./landing/TaglineReveal";
import FactoryJourney from "./landing/FactoryJourney";
import ForceMultiplier from "./landing/ForceMultiplier";
import BrandControl from "./landing/BrandControl";
import PlatformCoverage from "./landing/PlatformCoverage";
import ResultsSection from "./landing/ResultsSection";
import LandingFaq from "./landing/LandingFaq";
import FinalComposerCta from "./landing/FinalComposerCta";
import LandingFooter from "./landing/LandingFooter";
import { HERO_COPY } from "./landing/landingContent";
import { LANDING_EVENTS, trackLandingEvent } from "./landing/landingAnalytics";
import { usePrefersReducedMotion } from "./landing/useLandingReveal";
import {
  buildCheckoutReturnUrl,
  buildLandingPaywallReturnUrl,
} from "./lib/checkoutReturnUrls";

const DEFAULT_ASPECT_RATIO = "auto";
const PENDING_SUBMIT_STORAGE_KEY = "pendingSubmit";
const MEDIA_MODEL_SELECTION_STORAGE_KEY = "composerMediaModelSelection";
const SUBMIT_SUCCESS_DURATION_MS = 2000;

function readMediaModelSelection() {
  try {
    const value = JSON.parse(localStorage.getItem(MEDIA_MODEL_SELECTION_STORAGE_KEY));
    return value && Array.isArray(value.availableVideoModels) && Array.isArray(value.availableImageModels) ? value : null;
  } catch { return null; }
}

function readPendingSubmit() {
  const raw =
    sessionStorage.getItem(PENDING_SUBMIT_STORAGE_KEY) ||
    localStorage.getItem(PENDING_SUBMIT_STORAGE_KEY);
  if (!raw) return null;

  return JSON.parse(raw);
}

function writePendingSubmit(pendingData) {
  sessionStorage.setItem(PENDING_SUBMIT_STORAGE_KEY, JSON.stringify(pendingData));
  localStorage.removeItem(PENDING_SUBMIT_STORAGE_KEY);
}

function clearPendingSubmit() {
  sessionStorage.removeItem(PENDING_SUBMIT_STORAGE_KEY);
  localStorage.removeItem(PENDING_SUBMIT_STORAGE_KEY);
}

function appendAspectRatioToAgentPrompt(prompt, selectedAspectRatio) {
  return appendComposerAspectRatioToPrompt(prompt, selectedAspectRatio || DEFAULT_ASPECT_RATIO);
}

function getProjectInitAspectRatio(selectedAspectRatio) {
  // Persist "auto" on the project so exports preserve the source framing instead
  // of force-cropping to a fixed ratio. Generation falls back to a concrete ratio
  // server-side; only the export path treats "auto" as "no crop".
  return selectedAspectRatio || DEFAULT_ASPECT_RATIO;
}

async function fetchAttachmentsFromUrls(urls) {
  const files = [];
  for (const rawUrl of urls) {
    try {
      const response = await fetch(rawUrl);
      if (!response.ok) {
        console.error(`Failed to fetch attachment URL (${response.status}): ${rawUrl}`);
        continue;
      }
      const blob = await response.blob();
      const parsed = new URL(rawUrl);
      const pathSegments = parsed.pathname.split("/");
      let filename = decodeURIComponent(pathSegments[pathSegments.length - 1] || "");
      if (!filename || filename === "/") {
        const ext = (blob.type.split("/")[1] || "bin").split(";")[0];
        filename = `attachment.${ext}`;
      }
      files.push(
        new File([blob], filename, { type: blob.type || "application/octet-stream" })
      );
    } catch (err) {
      console.error(`Failed to fetch attachment URL: ${rawUrl}`, err);
    }
  }
  return files;
}

function LandingPage() {
  const navigate = useNavigate();
  const [attachments, setAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [promptText, setPromptText] = useState("");
  const [aspectRatio, setAspectRatio] = useState(DEFAULT_ASPECT_RATIO);
  const [multiAgent, setMultiAgent] = useState(false);
  const [extraCareful, setExtraCareful] = useState(false);
  const [mediaModelSelection, setMediaModelSelection] = useState(readMediaModelSelection);
  const [llmModel, setLlmModel] = useState(DEFAULT_COMPOSER_LLM_MODEL);
  const fileInputRef = useRef(null);
  const [fileObjects, setFileObjects] = useState([]);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAgentAutoReloadModal, setShowAgentAutoReloadModal] = useState(false);
  const [pendingAgentStart, setPendingAgentStart] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitFeedback, setSubmitFeedback] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [authRedirectPath, setAuthRedirectPath] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [billingUsage, setBillingUsage] = useState(null);
  const [checkingBilling, setCheckingBilling] = useState(false);
  const hasCheckedBilling = useRef(false);
  const hasAutoSubmittedFromUrl = useRef(false);
  const [urlAttachmentsLoading, setUrlAttachmentsLoading] = useState(false);
  const [optimisticProjects, setOptimisticProjects] = useState([]);
  const urlAttachmentUrlsRef = useRef([]);
  const submitLockRef = useRef(false);
  const submitFeedbackTimerRef = useRef(null);
  const isDarkTheme = useTheme();
  const reducedMotion = usePrefersReducedMotion();
  const { confirm: confirmAttachmentPreflight, dialog: attachmentPreflightDialog } = useAttachmentPreflightConfirmation();

  const [selectedReferences, setSelectedReferences] = useState([]);
  const heroTextareaRef = useRef(null);
  const finalTextareaRef = useRef(null);
  const heroSentinelRef = useRef(null);

  const {
    user,
    loading,
    openAuthModal,
    closeAuthModal,
    authModalOpen,
  } = useAuthContext();

  const referencePicker = useComposerReferencePicker({
    enabled: Boolean(user),
    value: promptText,
    onChange: setPromptText,
    textareaRef: heroTextareaRef,
    selectedReferences,
    setSelectedReferences,
  });

  // Meta Pixel + CAPI: ViewContent for landing page
  useEffect(() => {
    const eventId = trackMetaEvent('ViewContent', { content_type: 'landing' });
    sendCAPIEvent('ViewContent', eventId, { content_type: 'landing' }, getAdAttribution()).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) setSelectedReferences([]);
  }, [user]);

  const releaseSubmitLock = useCallback(() => {
    submitLockRef.current = false;
    setIsSubmitting(false);
  }, []);

  const resetComposerDraft = useCallback(() => {
    setPromptText("");
    setAttachments([]);
    setFileObjects([]);
    setSelectedReferences([]);
    urlAttachmentUrlsRef.current = [];
    setUrlAttachmentsLoading(false);
  }, []);

  const showSubmitSuccess = useCallback(() => {
    if (submitFeedbackTimerRef.current) {
      window.clearTimeout(submitFeedbackTimerRef.current);
    }
    setSubmitFeedback("success");
    submitFeedbackTimerRef.current = window.setTimeout(() => {
      setSubmitFeedback("progress");
      submitFeedbackTimerRef.current = null;
    }, SUBMIT_SUCCESS_DURATION_MS);
  }, []);

  useEffect(() => () => {
    if (submitFeedbackTimerRef.current) {
      window.clearTimeout(submitFeedbackTimerRef.current);
    }
  }, []);

  const addOptimisticProject = useCallback((project) => {
    setOptimisticProjects((current) => [
      project,
      ...current.filter((item) => (
        item.clientId !== project.clientId
        && (!project.projectId || item.projectId !== project.projectId)
      )),
    ]);
  }, []);

  const updateOptimisticProject = useCallback((clientId, updates) => {
    setOptimisticProjects((current) => current.map((project) => (
      project.clientId === clientId ? { ...project, ...updates } : project
    )));
  }, []);

  const removeOptimisticProject = useCallback((clientId) => {
    setOptimisticProjects((current) => current.filter((project) => project.clientId !== clientId));
  }, []);

  const isProjectLimitError = useCallback((error) => {
    if (!error) return false;
    return error.code === "FREE_PROJECT_LIMIT_REACHED" || (
      error.status === 402 && /free plan includes one project/i.test(error.diagnosticMessage || "")
    );
  }, []);

  const fetchBillingStatus = useCallback(async () => {
    if (checkingBilling) return;
    setCheckingBilling(true);
    try {
      const usage = await getBillingUsage();
      setBillingUsage(usage);
    } catch (_) {
      // Ignore usage failures; paywall can still render.
    } finally {
      hasCheckedBilling.current = true;
      setCheckingBilling(false);
    }
  }, [checkingBilling]);

  const openPaywall = useCallback(async () => {
    if (!hasCheckedBilling.current) {
      await fetchBillingStatus();
    }
    setPaywallOpen(true);
  }, [fetchBillingStatus]);

  const startAgentProjectCreation = useCallback(async ({
    promptText: rawPrompt,
    attachments: routeAttachments = [],
    attachmentUrls: routeAttachmentUrls = [],
    attachmentReferences: routeAttachmentReferences = [],
    aspectRatio: requestedAspectRatio,
    llmModel: requestedLlmModel,
    multiAgent: requestedMultiAgent = false,
    extraCareful: requestedExtraCareful = false,
    mediaModelSelection: requestedMediaModelSelection = null,
  }) => {
    const trimmedPrompt = (rawPrompt || "").trim();
    if (!trimmedPrompt && routeAttachments.length === 0 && routeAttachmentUrls.length === 0 && routeAttachmentReferences.length === 0) {
      return false;
    }

    let eligibility = null;
    try {
      eligibility = await getAgentEligibility();
    } catch (err) {
      console.error("Failed to fetch agent eligibility:", err);
    }

    const resolvedAspectRatio = requestedAspectRatio || aspectRatio;
    const projectInitAspectRatio = getProjectInitAspectRatio(resolvedAspectRatio);
    const resolvedLlmModel = requestedLlmModel || llmModel;

    if (eligibility && eligibility.allowed === false && eligibility.reason === "auto_reload_required") {
      setPendingAgentStart({
        promptText: trimmedPrompt,
        attachments: routeAttachments,
        attachmentUrls: routeAttachmentUrls,
        attachmentReferences: routeAttachmentReferences,
        aspectRatio: resolvedAspectRatio,
        llmModel: resolvedLlmModel,
        multiAgent: requestedMultiAgent,
        extraCareful: requestedExtraCareful,
        mediaModelSelection: requestedMediaModelSelection,
      });
      setShowAgentAutoReloadModal(true);
      return false;
    }

    if (requestedMultiAgent) {
      const clientId = `planning:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
      const createdAt = new Date().toISOString();
      addOptimisticProject({
        clientId,
        name: "",
        status: "scoping",
        launchStatus: "planning",
        canOpen: false,
        createdAt,
      });
      resetComposerDraft();
      showSubmitSuccess();
      releaseSubmitLock();
      try {
        const result = await startMultiAgentCompose(
          trimmedPrompt,
          routeAttachments,
          routeAttachmentUrls,
          {
            displayPrompt: trimmedPrompt,
            aspectRatio: resolvedAspectRatio,
            llmModel: resolvedLlmModel,
            extraCareful: requestedExtraCareful,
            attachmentReferences: routeAttachmentReferences,
            ...mediaModelRequestOptions(requestedMediaModelSelection),
          },
        );
        trackLandingEvent(LANDING_EVENTS.generationStarted, {
          source: 'landing', aspect_ratio: resolvedAspectRatio,
          multi_agent: true, project_count: result?.projects?.length || 1,
        });
        const launchedProjects = (result?.projects || []).map((project, index) => ({
          clientId: `multi:${project.project_id || index}`,
          projectId: project.project_id,
          name: project.project_name || "",
          status: "scoping",
          launchStatus: "running",
          latestAgentRunId: project.run_id || null,
          latestAgentRunStatus: project.status || "running",
          multiAgentGroupId: result.group_id || null,
          canOpen: Boolean(project.project_id),
          createdAt,
        }));
        setOptimisticProjects((current) => [
          ...launchedProjects,
          ...current.filter((project) => project.clientId !== clientId),
        ]);
        return true;
      } catch (err) {
        console.error("Failed to plan multi-agent compose run:", err);
        if (isProjectLimitError(err)) {
          removeOptimisticProject(clientId);
          await openPaywall();
          return true;
        }
        updateOptimisticProject(clientId, {
          launchStatus: "failed",
          errorMessage: "Composer could not plan these projects.",
        });
        return true;
      }
    }

    const projectId = Math.random().toString(36).substring(2, 15);
    const optimisticClientId = `project:${projectId}`;

    addOptimisticProject({
      clientId: optimisticClientId,
      projectId,
      name: "",
      status: "scoping",
      launchStatus: "starting",
      canOpen: false,
      createdAt: new Date().toISOString(),
    });
    resetComposerDraft();
    showSubmitSuccess();
    releaseSubmitLock();

    try {
      await initializeProject(projectId, trimmedPrompt, projectInitAspectRatio);
    } catch (err) {
      console.error("Failed to initialize project:", err);
      if (isProjectLimitError(err)) {
        removeOptimisticProject(optimisticClientId);
        await openPaywall();
        return true;
      }
      updateOptimisticProject(optimisticClientId, {
        launchStatus: "failed",
        errorMessage: "We could not start this workspace.",
      });
      return true;
    }

    updateOptimisticProject(optimisticClientId, { canOpen: true });

    // The card is already visible and the composer is usable again. Continue
    // upload processing and durable run creation without navigating away.
    const agentPrompt = appendAspectRatioToAgentPrompt(trimmedPrompt, resolvedAspectRatio);
    let composeResult = null;
    try {
      composeResult = await startAgentCompose(
        projectId,
        agentPrompt,
        routeAttachments,
        "pro",
        routeAttachmentUrls,
        null,
        {
          analyzeVideoAttachments: true,
          extraCareful: requestedExtraCareful,
          llmModel: resolvedLlmModel,
          attachmentReferences: routeAttachmentReferences,
          displayPrompt: trimmedPrompt,
          ...mediaModelRequestOptions(requestedMediaModelSelection),
        },
      );
    } catch (err) {
      console.error("Failed to start agent compose run:", err);
      updateOptimisticProject(optimisticClientId, {
        launchStatus: "failed",
        errorMessage: "Composer could not begin this video.",
      });
      return true;
    }

    updateOptimisticProject(optimisticClientId, {
      launchStatus: "running",
      latestAgentRunId: composeResult?.run_id || null,
      latestAgentRunStatus: composeResult?.status || "running",
    });

    trackLandingEvent(LANDING_EVENTS.generationStarted, {
      source: 'landing',
      aspect_ratio: resolvedAspectRatio,
    });

    return true;
  }, [
    addOptimisticProject,
    aspectRatio,
    isProjectLimitError,
    llmModel,
    openPaywall,
    releaseSubmitLock,
    removeOptimisticProject,
    resetComposerDraft,
    showSubmitSuccess,
    updateOptimisticProject,
  ]);

  const handleMediaModelSelectionChange = useCallback((selection) => {
    setMediaModelSelection(selection);
    try {
      if (selection) localStorage.setItem(MEDIA_MODEL_SELECTION_STORAGE_KEY, JSON.stringify(selection));
      else localStorage.removeItem(MEDIA_MODEL_SELECTION_STORAGE_KEY);
    } catch { /* selection still applies for this tab */ }
  }, []);

  const handleCheckout = useCallback(async () => {
    try {
      const returnUrl = buildLandingPaywallReturnUrl(promptText);

      const response = await setupBilling(
        buildCheckoutReturnUrl(returnUrl, "billing", "success"),
        buildCheckoutReturnUrl(returnUrl, "billing", "cancelled")
      );
      if (response?.checkoutUrl) {
        window.location.href = response.checkoutUrl;
      }
    } catch (err) {
      setSubmitError("Unable to start checkout. Please try again.");
    }
  }, [promptText]);

  const logoSrc = isDarkTheme ? '/latted_white.png' : '/latted_black.png';
  const logoIconSrc = isDarkTheme ? '/latted_icon_white.png' : '/latted_icon_black.png';

  // Effect to restore pending submission for this tab on mount
  useEffect(() => {
    try {
      const parsed = readPendingSubmit();
      if (parsed) {
        setPendingSubmit(parsed);
      }
    } catch (e) {
      console.error("Failed to parse pending submit:", e);
      clearPendingSubmit();
    }
  }, []);

  // Effect to handle pending submission after authentication
  useEffect(() => {
    // Wait for auth to finish loading
    if (loading) {
      return;
    }

    if (user && pendingSubmit) {
      // Reserve the public id before starting the same landing-card flow used
      // by an already authenticated submission.
      const projectId =
        pendingSubmit.projectId || Math.random().toString(36).substring(2, 15);

      // Prevent re-triggering this effect infinitely.
      if (pendingSubmit.redirectedToProject) {
        return;
      }

      try {
        const updated = { ...pendingSubmit, projectId, redirectedToProject: true };
        setPendingSubmit(updated);
        writePendingSubmit(updated);
      } catch {
        // ignore
      }

      if (authModalOpen) {
        closeAuthModal();
      }

      // Clear pending submit - project creation will happen through agent scoping flow
      const savedPending = { ...pendingSubmit };
      setPendingSubmit(null);
      clearPendingSubmit();

      submitLockRef.current = true;
      setIsSubmitting(true);

      const onComplete = (didStart) => {
        if (!didStart) releaseSubmitLock();
      };

      if (savedPending.attachmentUrls && savedPending.attachmentUrls.length > 0) {
        fetchAttachmentsFromUrls(savedPending.attachmentUrls).then((fetchedFiles) => {
          startAgentProjectCreation({
            promptText: savedPending.promptText,
            attachments: fetchedFiles,
            attachmentUrls: savedPending.attachmentUrls,
            attachmentReferences: savedPending.attachmentReferences || [],
            aspectRatio: savedPending.aspectRatio,
            llmModel: savedPending.llmModel,
            multiAgent: Boolean(savedPending.multiAgent),
            extraCareful: Boolean(savedPending.extraCareful),
            mediaModelSelection: savedPending.mediaModelSelection || null,
          }).then(onComplete);
        });
      } else {
        startAgentProjectCreation({
          promptText: savedPending.promptText,
          attachments: savedPending.attachments,
          attachmentReferences: savedPending.attachmentReferences || [],
          aspectRatio: savedPending.aspectRatio,
          llmModel: savedPending.llmModel,
          multiAgent: Boolean(savedPending.multiAgent),
          extraCareful: Boolean(savedPending.extraCareful),
          mediaModelSelection: savedPending.mediaModelSelection || null,
        }).then(onComplete);
      }
    }
  }, [loading, user, pendingSubmit, navigate, closeAuthModal, authModalOpen, startAgentProjectCreation, releaseSubmitLock]);

  useEffect(() => {
    if (!user || !authRedirectPath || pendingSubmit) return;
    const redirectPath = authRedirectPath;
    setAuthRedirectPath(null);
    navigate(redirectPath);
  }, [authRedirectPath, navigate, pendingSubmit, user]);

  useEffect(() => {
    if (!user || pendingSubmit) return;
    const returnPath = sessionStorage.getItem("auth_return_path");
    if (!returnPath) return;
    sessionStorage.removeItem("auth_return_path");
    navigate(returnPath);
  }, [navigate, pendingSubmit, user]);

  const isTruthyParam = useCallback((value) => {
    if (!value) return false;
    const normalized = value.trim().toLowerCase();
    return ["1", "true", "yes", "on"].includes(normalized);
  }, []);

  // Keep scrolling scoped to UniversalLayout's content pane.
  useEffect(() => {
    const body = document.body;
    const previousOverflow = body.style.overflow;

    body.style.overflow = "";

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, []);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const addFiles = async (files) => {
    setFileError(null);
    const { accepted, errors } = await validateComposerFiles(files, fileObjects);
    if (accepted.length > 0) {
      setAttachments((current) => [...current, ...accepted.map((file) => composerAttachmentChip(file))]);
      setFileObjects((current) => [...current, ...accepted]);
    }
    if (errors.length > 0) {
      setFileError(errors[0]);
      setTimeout(() => setFileError(null), 6000);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    e.target.value = "";
    addFiles(files);
  };

  const removeAttachment = (id) => {
    const index = attachments.findIndex((att) => att.id === id);
    setAttachments(attachments.filter((att) => att.id !== id));
    if (index !== -1) {
      setFileObjects(fileObjects.filter((_, i) => i !== index));
    }
  };

  const handleRemoveReference = useCallback((reference) => {
    setSelectedReferences((current) =>
      current.filter((item) => item.kind !== reference.kind || item.id !== reference.id)
    );
  }, []);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const isSubmitBlocked = () =>
    referencePicker.open ||
    (!promptText.trim() && attachments.length === 0 && selectedReferences.length === 0);

  const handleKeyDown = (e) => {
    if (referencePicker.handleKeyDown(e)) return;
    // Submit on CMD+RETURN (Mac) or CTRL+RETURN (Windows/Linux)
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      if (isSubmitBlocked()) return;
      // Keyboard events originate from a textarea, so derive the location from
      // which composer instance is focused rather than the form's data attribute.
      const submitLocation = e.currentTarget === finalTextareaRef.current ? "final" : "hero";
      handleConfirmSubmitPrompt(undefined, submitLocation);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmitBlocked()) {
      return;
    }
    const submitLocation = e.currentTarget?.dataset?.location || "hero";
    handleConfirmSubmitPrompt(undefined, submitLocation);
  };

  const handleCreateFromScratch = useCallback(async () => {
    setSubmitError(null);
    setIsSubmitting(true);

    if (!user) {
      sessionStorage.setItem("auth_return_path", "/projects");
      setAuthRedirectPath("/projects");
      openAuthModal();
      setIsSubmitting(false);
      return;
    }

    try {
      const projectId = Math.random().toString(36).substring(2, 15);
      await createBlankProject(projectId);
      navigate(`/project/${projectId}`, {
        state: {
          promptText: "",
          attachments: [],
          isBlankProject: true,
        },
      });
    } catch (err) {
      console.error("Failed to create blank project from landing:", err);
      if (isProjectLimitError(err)) {
        setIsSubmitting(false);
        await openPaywall();
        return;
      }
      setSubmitError("Failed to create blank project");
      setIsSubmitting(false);
    }
  }, [
    isProjectLimitError,
    navigate,
    openAuthModal,
    openPaywall,
    user,
  ]);

  const handleEnableAutoReloadAndContinue = useCallback(async ({ thresholdCents, amountCents }) => {
    await updateCreditSettings({
      autoReloadEnabled: true,
      autoReloadThresholdCents: thresholdCents,
      autoReloadAmountCents: amountCents,
    });

    const pending = pendingAgentStart;
    setShowAgentAutoReloadModal(false);
    setPendingAgentStart(null);

    if (pending) {
      await startAgentProjectCreation(pending);
    }
  }, [pendingAgentStart, startAgentProjectCreation]);

  const handleConfirmSubmitPrompt = useCallback(async (promptOverride, submitLocation = "hero") => {
    if (submitLockRef.current) {
      return;
    }

    const normalizedPrompt = (promptOverride ?? promptText).trim();

    if (!normalizedPrompt && attachments.length === 0 && selectedReferences.length === 0) {
      releaseSubmitLock();
      return;
    }

    setSubmitError(null);

    const hasAttachment =
      fileObjects.length > 0 || urlAttachmentUrlsRef.current.length > 0;

    trackLandingEvent(LANDING_EVENTS.promptSubmitted, {
      authenticated: Boolean(user),
      location: submitLocation,
      has_attachment: hasAttachment,
      aspect_ratio: aspectRatio,
      multi_agent: multiAgent,
      extra_careful: extraCareful,
    });
    trackLandingEvent(LANDING_EVENTS.composerSubmitted, {
      selection: mediaModelSelection?.selectionSource || 'auto',
      video_count: mediaModelSelection?.availableVideoModels?.length || 0,
      image_count: mediaModelSelection?.availableImageModels?.length || 0,
      has_preset: Boolean(mediaModelSelection?.presetId),
      multi_agent: multiAgent,
      extra_careful: extraCareful,
    });

    if (!user) {
      sessionStorage.removeItem("auth_return_path");
      const projectId = Math.random().toString(36).substring(2, 15);
      const pendingData = {
        projectId,
        promptText: normalizedPrompt,
        attachments: [],
        attachmentUrls: urlAttachmentUrlsRef.current.length > 0
          ? [...urlAttachmentUrlsRef.current]
          : undefined,
        attachmentReferences: toAttachmentReferences(selectedReferences),
        hasAttachments: fileObjects.length > 0,
        redirectedToProject: false,
        aspectRatio,
        llmModel,
        multiAgent,
        extraCareful,
        mediaModelSelection,
      };
      setPendingSubmit(pendingData);
      writePendingSubmit(pendingData);
      openAuthModal();
      trackLandingEvent(LANDING_EVENTS.authOpenedFromPrompt, {
        has_attachment: hasAttachment,
        aspect_ratio: aspectRatio,
      });
      releaseSubmitLock();
      return;
    }

    submitLockRef.current = true;
    setIsSubmitting(true);

    const attachmentReferences = toAttachmentReferences(selectedReferences);
    if (attachmentReferences.some((reference) => reference.kind === 'space' || reference.kind === 'space_node')) {
      try {
        const response = await preflightAttachmentReferences(attachmentReferences);
        const preflight = response.preflight || {};
        const confirmed = await confirmAttachmentPreflight(preflight);
        if (!confirmed) { setIsSubmitting(false); releaseSubmitLock(); return; }
      } catch (err) {
        setSubmitError('Could not validate Space attachments.');
        setIsSubmitting(false); releaseSubmitLock(); return;
      }
    }

    const didStartProject = await startAgentProjectCreation({
      promptText: normalizedPrompt,
      attachments: fileObjects,
      attachmentUrls: urlAttachmentUrlsRef.current.length > 0 ? urlAttachmentUrlsRef.current : [],
      attachmentReferences,
      aspectRatio,
      llmModel,
      multiAgent,
      extraCareful,
      mediaModelSelection,
    });

    if (!didStartProject) {
      releaseSubmitLock();
    }
  }, [
    aspectRatio,
    attachments.length,
    confirmAttachmentPreflight,
    fileObjects,
    openAuthModal,
    promptText,
    releaseSubmitLock,
    selectedReferences,
    llmModel,
    multiAgent,
    extraCareful,
    mediaModelSelection,
    startAgentProjectCreation,
    user,
  ]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const billingStatus = urlParams.get("billing");
    const creditsStatus = urlParams.get("credits");
    const subscriptionStatus = urlParams.get("subscription");
    const sessionId = urlParams.get("session_id");
    const billingPrompt = urlParams.get("billingPrompt");
    const autoPromptParam = urlParams.get("autoprompt");
    const shouldAutoSubmit = isTruthyParam(urlParams.get("autosubmit"));
    const attachmentUrls = urlParams.getAll("attachment");
    const urlPrompt = (autoPromptParam || billingPrompt || "").trim();

    if (urlPrompt) {
      setPromptText(urlPrompt);
    }

    let attachmentsStillLoading = urlAttachmentsLoading;
    if (attachmentUrls.length > 0 && urlAttachmentUrlsRef.current.length === 0) {
      urlAttachmentUrlsRef.current = attachmentUrls;
      setUrlAttachmentsLoading(true);
      attachmentsStillLoading = true;
    }

    if (
      !loading &&
      !attachmentsStillLoading &&
      shouldAutoSubmit &&
      urlPrompt &&
      !hasAutoSubmittedFromUrl.current
    ) {
      hasAutoSubmittedFromUrl.current = true;
      handleConfirmSubmitPrompt(urlPrompt);
    }

    if (!billingStatus && !creditsStatus && !subscriptionStatus) return;

    const handleBillingReturn = async () => {
      if (creditsStatus === "success" && sessionId) {
        try {
          const result = await confirmCreditPurchase(sessionId);
          const purchaseValue = result?.creditedCents;
          if (purchaseValue) {
            trackMetaEvent('Purchase', {
              value: purchaseValue / 100,
              currency: 'USD',
              content_type: 'credits',
              order_id: sessionId,
              num_items: 1,
            }, `purchase_${sessionId}`);
          }
        } catch (err) {
          console.error("Failed to confirm credit purchase:", err);
        }
      }

      if (subscriptionStatus === "success" && sessionId) {
        try {
          await confirmSubscription(sessionId);
        } catch (err) {
          console.error("Failed to confirm subscription:", err);
        }
      }

      if (
        billingStatus === "success" ||
        creditsStatus === "success" ||
        subscriptionStatus === "success"
      ) {
        setPaywallOpen(false);
        hasCheckedBilling.current = false;
        fetchBillingStatus();
      }

      window.history.replaceState({}, "", window.location.pathname);
    };

    handleBillingReturn();
  }, [
    fetchBillingStatus,
    handleConfirmSubmitPrompt,
    isTruthyParam,
    loading,
    urlAttachmentsLoading,
  ]);

  useEffect(() => {
    const urls = urlAttachmentUrlsRef.current;
    if (urls.length === 0 || !urlAttachmentsLoading) return;

    let cancelled = false;

    (async () => {
      const fetchedFiles = await fetchAttachmentsFromUrls(urls);
      if (cancelled) return;

      if (fetchedFiles.length > 0) {
        const newAttachments = fetchedFiles.map((file) => ({
          id: Math.random().toString(36).substr(2, 9),
          name: file.name,
          size: file.size,
          type: file.type,
          extension: file.name.split(".").pop().toUpperCase(),
        }));
        setAttachments((prev) => [...prev, ...newAttachments]);
        setFileObjects((prev) => [...prev, ...fetchedFiles]);
      }

      setUrlAttachmentsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [urlAttachmentsLoading]);

  /* ─── Landing-specific UI behavior ─── */

  const handleThemeToggle = useCallback((nextTheme) => {
    document.documentElement.setAttribute('data-theme', nextTheme);
    try {
      localStorage.setItem('theme', nextTheme);
    } catch {
      // storage unavailable; theme still applies for this session
    }
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', nextTheme === 'dark' ? '#131209' : '#F6F0E5');
    }
    trackLandingEvent(LANDING_EVENTS.themeChanged, { theme: nextTheme });
  }, []);

  const handleComposerFocus = useCallback((location) => {
    try {
      if (sessionStorage.getItem('lp_composer_focus_tracked')) return;
      sessionStorage.setItem('lp_composer_focus_tracked', '1');
    } catch {
      // ignore storage failures and still avoid double tracking per mount
    }
    trackLandingEvent(LANDING_EVENTS.composerFocused, { location });
  }, []);

  const handleApplySuggestion = useCallback((suggestion, location) => {
    setPromptText(suggestion.prompt);
    const targetRef = location === 'final' ? finalTextareaRef : heroTextareaRef;
    requestAnimationFrame(() => {
      const el = targetRef.current;
      if (!el) return;
      el.focus();
      const end = suggestion.prompt.length;
      try {
        el.setSelectionRange(end, end);
      } catch {
        // ignore caret failures on unsupported inputs
      }
    });
  }, []);

  const focusComposer = useCallback((which) => {
    const el = which === 'final' ? finalTextareaRef.current : heroTextareaRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
    window.setTimeout(() => el.focus({ preventScroll: true }), reducedMotion ? 0 : 350);
  }, [reducedMotion]);

  const handleCreateVideo = useCallback(() => {
    if (user) {
      focusComposer('hero');
      return;
    }
    const heroRect = heroTextareaRef.current?.getBoundingClientRect();
    const finalRect = finalTextareaRef.current?.getBoundingClientRect();
    if (!heroRect) return;
    const viewportCenter = window.innerHeight / 2;
    const heroDistance = Math.abs(heroRect.top + heroRect.height / 2 - viewportCenter);
    const finalDistance = finalRect
      ? Math.abs(finalRect.top + finalRect.height / 2 - viewportCenter)
      : Infinity;
    focusComposer(finalDistance < heroDistance ? 'final' : 'hero');
  }, [focusComposer, user]);

  const handleSignIn = useCallback(() => {
    sessionStorage.removeItem("auth_return_path");
    openAuthModal();
  }, [openAuthModal]);

  const handleOpenLandingProject = useCallback((project) => {
    navigate(`/project/${project.projectId}`, {
      state: {
        agentMode: true,
        agentRunId: project.latestAgentRunId || null,
        multiAgentGroupId: project.multiAgentGroupId || null,
      },
    });
  }, [navigate]);

  const canSubmit = Boolean(
    promptText.trim() || attachments.length > 0 || selectedReferences.length > 0
  );

  useEffect(() => {
    if (submitFeedback && canSubmit) {
      setSubmitFeedback(null);
    }
  }, [canSubmit, submitFeedback]);

  const composerError = fileError || submitError || null;

  const baseComposerProps = {
    promptText,
    onPromptChange: referencePicker.handlePromptChange,
    onKeyDown: handleKeyDown,
    onSubmit: handleSubmit,
    picker: referencePicker,
    references: selectedReferences,
    onRemoveReference: handleRemoveReference,
    attachments,
    onRemoveAttachment: removeAttachment,
    attachmentsLoading: urlAttachmentsLoading,
    aspectRatio,
    onAspectRatioChange: setAspectRatio,
    multiAgent,
    onMultiAgentChange: setMultiAgent,
    extraCareful,
    onExtraCarefulChange: setExtraCareful,
    mediaModelSelection,
    onMediaModelSelectionChange: handleMediaModelSelectionChange,
    llmModel,
    onLlmModelChange: setLlmModel,
    authenticated: Boolean(user),
    fileInputRef,
    onFileInput: handleFileInput,
    onAttachClick: () => fileInputRef.current?.click(),
    isSubmitting,
    submitFeedback,
    isDragging,
    canSubmit,
    errorMessage: composerError,
    formatFileSize,
    onApplySuggestion: handleApplySuggestion,
    onComposerFocus: handleComposerFocus,
  };

  const dragHandlers = {
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDragOver: handleDragOver,
    onDrop: handleDrop,
  };

  const sharedModals = (
    <>
      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        onCheckout={handleCheckout}
        usage={billingUsage}
        billingMode={billingUsage?.billingMode}
        checkoutReturnUrl={buildLandingPaywallReturnUrl(promptText)}
      />
      <AgentAutoReloadModal
        isOpen={showAgentAutoReloadModal}
        onClose={() => {
          setShowAgentAutoReloadModal(false);
          setPendingAgentStart(null);
        }}
        onEnableAutoReload={handleEnableAutoReloadAndContinue}
        onCreateBlankProject={async () => {
          setShowAgentAutoReloadModal(false);
          setPendingAgentStart(null);
          await handleCreateFromScratch();
        }}
      />
      {attachmentPreflightDialog}
    </>
  );

  if (user) {
    return (
      <UniversalLayout>
        <div className="lp-page lp-page--app" {...dragHandlers}>
          <main className="lp-app-home">
            <div className="lp-container lp-app-home__inner">
              <h1 className="lp-heading lp-app-home__title">What do you want to create?</h1>
              <LandingComposer
                {...baseComposerProps}
                textareaRef={heroTextareaRef}
                location="home"
                placeholder={HERO_COPY.placeholder}
              />
              <LandingProjectRuns
                userId={user.id}
                optimisticProjects={optimisticProjects}
                onOpenProject={handleOpenLandingProject}
                onViewAll={() => navigate('/projects')}
              />
            </div>
          </main>
          {sharedModals}
        </div>
      </UniversalLayout>
    );
  }

  return (
    <div className="lp-page" {...dragHandlers}>
      <a href="#lp-main" className="lp-skip-link">
        Skip to content
      </a>
      <LandingNavigation
        logoSrc={logoSrc}
        iconSrc={logoIconSrc}
        isDark={isDarkTheme}
        onToggleTheme={handleThemeToggle}
        onSignIn={handleSignIn}
        onCreateVideo={handleCreateVideo}
        sentinelRef={heroSentinelRef}
      />
      <main id="lp-main" tabIndex={-1}>
        <LandingHero
          composerProps={{ ...baseComposerProps, textareaRef: heroTextareaRef }}
          sentinelRef={heroSentinelRef}
          decoration={<HeroFloatingIcons />}
        />
        <OutputShowcase />
        <ComposerAgentShowcase />
        <TaglineReveal />
        <ForceMultiplier />
        <FactoryJourney />
        <BrandControl />
        <PlatformCoverage />
        <ResultsSection />
        <LandingFaq />
        <FinalComposerCta
          composerProps={{ ...baseComposerProps, textareaRef: finalTextareaRef }}
        />
      </main>
      <LandingFooter
        logoSrc={logoSrc}
        isDark={isDarkTheme}
        onToggleTheme={handleThemeToggle}
        onSignIn={handleSignIn}
        onCreateVideo={handleCreateVideo}
      />
      {sharedModals}
    </div>
  );
}

export default LandingPage;
